import React, { useState, useEffect } from 'react';
import { 
  User, 
  Mail, 
  Lock, 
  Shield, 
  CreditCard, 
  Crown, 
  Sparkles,
  Settings,
  LogOut,
  CheckCircle2,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { getCurrentUser, fetchUserAttributes, updateUserAttribute, updatePassword, signOut } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import TwoFactorSetup from './TwoFactorSetup';

export default function UserProfile({ onClose }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userAttributes, setUserAttributes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      
      const attributes = await fetchUserAttributes();
      setUserAttributes(attributes);

    } catch (err) {
      console.error('Error loading user data:', err);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  // Password change form component
  const PasswordChangeForm = ({ onSuccess, onError }) => {
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      
      if (newPassword !== confirmPassword) {
        onError({ message: 'New passwords do not match' });
        setLoading(false);
        return;
      }

      if (newPassword.length < 8) {
        onError({ message: 'Password must be at least 8 characters' });
        setLoading(false);
        return;
      }

      try {
        await updatePassword({ oldPassword, newPassword });
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        onSuccess();
      } catch (err) {
        onError(err);
      } finally {
        setLoading(false);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-slate-300 mb-2">Current Password</label>
          <input
            type="password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-300 mb-2">New Password</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            required
            minLength={8}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-300 mb-2">Confirm New Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-xl p-3 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            required
            minLength={8}
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-green-500 text-white font-bold py-3 px-4 rounded-xl hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Updating...' : 'Update Password'}
        </button>
      </form>
    );
  };



  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/dashboard');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const getTierDisplay = () => ({name:'Free account',icon:<User className="h-5 w-5 text-green-400" />,color:'text-green-400',bgColor:'bg-green-500/10',borderColor:'border-green-500/30'});

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-green-400" />
      </div>
    );
  }

  const tier = getTierDisplay();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-extrabold bg-gradient-to-r from-green-400 to-green-300 bg-clip-text text-transparent">
              Account Settings
            </h1>
            <p className="text-slate-400 mt-1">Manage your account and subscription</p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <Settings className="h-6 w-6" />
            </button>
          )}
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 rounded-xl bg-green-500/10 border border-green-500/30 p-4 flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-400" />
            <p className="text-green-400">{success}</p>
          </div>
        )}
        {error && (
          <div className="mb-6 rounded-xl bg-red-500/10 border border-red-500/30 p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-800">
          {[
            { id: 'profile', label: 'Profile', icon: User },
            { id: 'subscription', label: 'Account & billing', icon: CreditCard },
            { id: 'security', label: 'Security', icon: Shield },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-3 font-semibold text-sm transition-colors border-b-2 ${
                activeTab === id
                  ? 'border-green-400 text-green-400'
                  : 'border-transparent text-slate-400 hover:text-slate-300'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/60 to-slate-900/40 p-6">
              <h2 className="text-xl font-bold text-white mb-4">Profile Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Email</label>
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-slate-400" />
                    <span className="text-white">{userAttributes?.email || user?.username || 'N/A'}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Email cannot be changed here. Contact support if needed.</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">User ID</label>
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-slate-400" />
                    <span className="text-white font-mono text-sm">{user?.userId || user?.username || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'subscription' && <section className="rounded-2xl border border-slate-800 p-6 text-slate-300">
          <h2 className="text-xl font-bold text-white">Free Tekyu account</h2>
          <p className="my-4">Tekyu no longer sells subscriptions. Roadmaps, projects and saved progress are available through your free account.</p>
          <h3 className="font-semibold text-white">Previously paid for Tekyu?</h3>
          <p className="my-3">Moving to free access does not automatically cancel an existing Stripe subscription. Contact billing support to check a previous payment or request cancellation.</p>
          <a className="text-green-400" href="mailto:support@tekyu.co.uk?subject=Tekyu%20previous%20subscription">Contact billing support</a>
        </section>}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/60 to-slate-900/40 p-6">
              <h2 className="text-xl font-bold text-white mb-4">Change Password</h2>
              <PasswordChangeForm 
                onSuccess={() => {
                  setSuccess('Password changed successfully');
                  setTimeout(() => setSuccess(null), 3000);
                }}
                onError={(err) => {
                  setError(err.message || 'Failed to change password');
                  setTimeout(() => setError(null), 5000);
                }}
              />
            </div>

            <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/60 to-slate-900/40 p-6">
              <h2 className="text-xl font-bold text-white mb-4">Two-Factor Authentication</h2>
              <p className="text-slate-400 mb-4">
                2FA adds an extra layer of security to your account. This feature will be available soon.
              </p>
              <TwoFactorSetup onComplete={() => { setSuccess('2FA enabled successfully!'); setTimeout(() => setSuccess(null), 3000); }} />
            </div>
          </div>
        )}

        {/* Sign Out */}
        <div className="mt-8 pt-6 border-t border-slate-800">
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 text-slate-400 hover:text-red-400 transition-colors"
          >
            <LogOut className="h-5 w-5" />
            <span>Sign Out</span>
          </button>
        </div>
      </div>


    </div>
  );
}
